<script setup>
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import { VDataTableServer } from 'vuetify/labs/VDataTable'
import OrganismDrawer from '@/views/quality/master/OrganismDrawer.vue'
import OrganismService from '@/services/quality/organism.service'

definePage({
  meta: {
    subject: 'quality.infection_control.master',
    action: 'organism',
  },
})

const searchQuery = ref('')
const debouncedSearch = ref('')
const isConfirmDialogVisible = ref(false)

// Debounce search 500ms
let searchTimeout = null

watch(searchQuery, val => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    debouncedSearch.value = val
    page.value = 1
  }, 500)
})

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()

const updateOptions = options => {
  page.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

// Headers
const headers = [
  { title: 'Name', key: 'name' },
  { title: 'Code', key: 'code' },
  { title: 'Type', key: 'type' },
  { title: 'Gram Type', key: 'gram_type' },
  { title: 'MDRO', key: 'is_mdro', sortable: false },
  { title: 'Status', key: 'status', sortable: false },
  { title: 'Actions', key: 'actions', sortable: false },
]

const {
  data: organismListing,
  execute: fetchOrganismListing,
} = await useApi(createUrl('/masters/organisms', {
  query: {
    paged: 1,
    q: debouncedSearch,
    itemsPerPage,
    page,
    sort: sortBy,
    order_by: orderBy,
  },
}))

const organisms = computed(() => organismListing.value?.data)
const totalOrganisms = computed(() => organismListing.value?.meta?.total)

const isDrawerVisible = ref(false)
const isViewOnly = ref(false)
const organismData = ref()

const showOrganism = (data = null, viewOnly = false) => {
  organismData.value = data
  isDrawerVisible.value = true
  isViewOnly.value = viewOnly
}

watch(isDrawerVisible, value => {
  if (value === false) {
    organismData.value = null
    isViewOnly.value = false
  }
})

const refConfirmDialog = ref()

const showConfirmDialog = data => {
  organismData.value = data
  isConfirmDialogVisible.value = true
}

const deleteOrganism = async (confirm, data) => {
  if (data?.id && confirm) {
    OrganismService.destroy(
      data.id,
      () => {
        refConfirmDialog.value.onSuccess()
        isConfirmDialogVisible.value = false
        fetchOrganismListing()
      },
      () => {
        refConfirmDialog.value.onError()
      },
    )
  }
}

watch(isConfirmDialogVisible, value => {
  if (value === false) organismData.value = null
})
</script>

<template>
  <section>
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: 'All' },
            ]"
            style="inline-size: 6.25rem;"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search -->
          <div style="inline-size: 10rem;">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>

          <VBtn
            prepend-icon="tabler-plus"
            @click="showOrganism(null)"
          >
            Add Organism
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :items="organisms"
        :items-length="totalOrganisms"
        :headers="headers"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <template #item.name="{ item }">
          <div
            class="d-flex align-center cursor-pointer"
            @click="showOrganism(item, true)"
          >
            <VAvatar
              size="34"
              variant="tonal"
              class="me-3"
            >
              <span>{{ avatarText(item.name) }}</span>
            </VAvatar>
            <div class="d-flex flex-column">
              <h6 class="text-base">
                {{ item.name }}
              </h6>
            </div>
          </div>
        </template>

        <template #item.is_mdro="{ item }">
          <VChip
            v-if="item.is_mdro"
            color="error"
            size="small"
          >
            MDRO
          </VChip>
          <span
            v-else
            class="text-medium-emphasis"
          >—</span>
        </template>

        <template #item.status="{ item }">
          <VChip
            :color="item.status ? 'success' : 'error'"
            size="small"
          >
            {{ item.status ? 'Active' : 'Inactive' }}
          </VChip>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn @click="showConfirmDialog(item)">
            <VIcon icon="tabler-trash" />
          </IconBtn>

          <IconBtn @click="showOrganism(item)">
            <VIcon icon="tabler-edit" />
          </IconBtn>
        </template>
      </VDataTableServer>
    </VCard>

    <OrganismDrawer
      v-model:isDrawerOpen="isDrawerVisible"
      :form-data="organismData"
      :view-only="isViewOnly"
      @refetch-listing="fetchOrganismListing"
    />
    <ConfirmDialog
      ref="refConfirmDialog"
      v-model:isDialogVisible="isConfirmDialogVisible"
      confirmation-question="Are you sure?"
      confirm-msg="Organism Deleted."
      confirm-title="Deleted!"
      cancel-title="Error!"
      cancel-msg="Something Went Wrong."
      :is-confirm-visible="false"
      :is-error-visible="false"
      :data="organismData"
      @confirm="deleteOrganism"
    />
  </section>
</template>
